import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useGoBack } from "../../../../shared/phone/history";
import { useRail } from "../../../../shared/phone/media";
import { PhoneNav } from "../../../../shared/phone/PhoneNav";
import { PnRow } from "../../../../shared/phone/Row";
import { Split } from "../../../../shared/phone/Split";
import { NewGoalSheet } from "../../../components/NewGoalMenu";
import { Icon } from "../../../components/ui/Icon";
import type { Dates, GoalNumbers, HomeGoal, HomeProduct } from "../../../lib/ccApi";
import { fmt, shortGoal, usd } from "../text";
import { useHome } from "../useCc";
import { datesInWords, datesOf, daysWords } from "../v2";
import { AskSheet } from "./AskSheet";
import { NeedPane, NeedSheet, type NeedFlowProps } from "./NeedView";
import { greeting, longToday, rangeLabel, running, standing, totals, useNumbers } from "./numbers";
import { closeSheet, Count, GoalRow, ProductMark, useHmToast, WaitRow } from "./parts";
import { RangeSheet } from "./RangeSheet";
import { inGoalOrder, waitingRows, type Waiting } from "./waiting";
import { Can, useCan } from "../../../context/AuthContext";

/**
 * Home on a phone and an iPad: the greeting, the dates and one sentence about them, what needs the owner,
 * then each product with its goals. On an iPad the list sits beside a goal's page, or beside the need picked.
 */

const enc = encodeURIComponent;

// --- Shared with one product's screen ---------------------------------------------------------------------

/** The sheets Home opens: Ask, New goal and the dates. */
export type HomeSheet = "ask" | "new" | "range" | null;

/** Ask and New, on the title's line, each with its word. */
export function HomeActions({ open }: { open: (s: HomeSheet) => void }) {
  // Why for an Editor: asking writes to a goal's chat, and New starts a goal.
  return (
    <Can min="editor">
      <button type="button" className="pn-tbtn word" onClick={() => open("ask")}>
        <Icon name="ask" />
        Ask
      </button>
      <button type="button" className="pn-tbtn pill" onClick={() => open("new")} aria-label="New goal">
        <Icon name="plus" />
        New
      </button>
    </Can>
  );
}

/** The dates button, then one sentence: what the money brought over those dates, and how the running goals stand. */
export function Summary({ goals, nums, dates, onDates, who, loaded }: { goals: HomeGoal[]; nums: Map<string, GoalNumbers>; dates: Dates; onDates: () => void; who?: string; loaded: boolean }) {
  const { spent, results } = totals(goals, nums);
  const s = standing(goals, nums);
  const pace =
    s.run === 0 ? null : s.run === 1 ? (
      <>
        {" "}
        The running goal is <b className={s.on ? "ok" : "w"}>{s.on ? "on pace" : "behind"}</b>.
      </>
    ) : (
      <>
        {" "}
        <b className={s.on === s.run ? "ok" : ""}>
          {s.on} of {s.run}
        </b>{" "}
        running goals are on pace{s.behind ? <>, <b className="w">{s.behind} behind</b></> : null}.
      </>
    );
  return (
    <section className="pn-pad hm-sum">
      <button type="button" className="hm-range" onClick={onDates}>
        <Icon name="calendar" />
        <span>{rangeLabel(dates) || daysWords(datesOf(dates, null))}</span>
        <Icon name="chev" />
      </button>
      {/* Why: until the numbers have been read, "nothing spent" would be a wrong thing to say. */}
      {loaded && (nums.size > 0 || !goals.length) && (
        <p className="hm-lead">
          {spent > 0 ? (
            <>
              {who ? `${who} spent` : "You spent"} <b>{usd(Math.round(spent / 100) * 100)}</b> and got <b>{fmt(results)}</b> result{results === 1 ? "" : "s"}
              {results > 0 ? (
                <>
                  , <b>{usd(Math.round(spent / results))}</b> each
                </>
              ) : null}
              .{pace}
            </>
          ) : (
            <>Nothing spent {datesInWords(dates, rangeLabel(dates))}.{pace}</>
          )}
        </p>
      )}
    </section>
  );
}

/** Ask, New goal and the dates, as sheets over the screen. */
export function HomeSheets({
  sheet,
  close,
  products,
  dates,
  setDates,
  newTo,
  say,
}: {
  sheet: HomeSheet;
  close: () => void;
  products: HomeProduct[];
  dates: Dates;
  setDates: (d: Dates) => void;
  newTo: { to: string; coldEmail: boolean };
  say: (m: string, ok?: boolean) => void;
}) {
  return (
    <>
      <AskSheet open={sheet === "ask"} onClose={close} products={products} say={say} />
      <NewGoalSheet open={sheet === "new"} onClose={close} to={newTo.to} coldEmail={newTo.coldEmail} />
      {sheet === "range" && (
        <RangeSheet
          open
          onClose={close}
          dates={dates}
          onChange={(d) => {
            setDates(d);
            const label = rangeLabel(d);
            say(`Showing ${label ? label.toLowerCase() : daysWords(datesOf(d, null))}`);
          }}
        />
      )}
    </>
  );
}

/**
 * The need named in the address, kept while its sheet slides away. Why: once decided it leaves the list at
 * once, but the sheet still shows it until it has closed.
 */
export function useOpenNeed(rows: Waiting[], key: string | null): Waiting | null {
  const last = useRef<Waiting | null>(null);
  const found = key ? (rows.find((r) => r.key === key) ?? (last.current?.key === key ? last.current : null)) : null;
  last.current = found;
  return found;
}

/** What spent over the dates, under a product's name. */
function spentLine(p: HomeProduct, nums: Map<string, GoalNumbers>, dates: Dates): ReactNode {
  const { spent } = totals(p.goals, nums);
  const words = datesInWords(dates, rangeLabel(dates));
  return spent > 0 ? (
    <>
      <b>{usd(Math.round(spent / 100) * 100)}</b> spent {words}
    </>
  ) : p.goals.length ? (
    `Nothing spent ${words}`
  ) : (
    "No goals yet"
  );
}

/** One product: its row opens the product, then each goal opens its own page. */
function ProductGroup({ p, nums, dates, goalOn }: { p: HomeProduct; nums: Map<string, GoalNumbers>; dates: Dates; goalOn: string | null }) {
  return (
    <section>
      <div className="pn-grp">
        <PnRow to={`/products/${enc(p.id)}`} className="hm-prod" icon={<ProductMark p={p} size={34} />} title={p.name} sub={spentLine(p, nums, dates)} />
        {p.goals.map((g) => (
          <GoalRow key={g.id} g={g} n={nums.get(g.id)} on={goalOn === g.id} />
        ))}
        {!p.goals.length && (
          <Can min="editor">
            <PnRow to={`/products/${enc(p.id)}/goals/new`} className="accent" icon={<Icon name="plus" />} title="New goal" />
          </Can>
        )}
      </div>
    </section>
  );
}

// --- Home ------------------------------------------------------------------------------------------------

/** Home (`/command`), and on an iPad also a goal's own page beside it (`/goals/:goalId`, given as `goal`). */
export function HomeNative({ goal }: { goal?: ReactNode }) {
  const rail = useRail();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { goalId = null } = useParams();
  const [params] = useSearchParams();
  const { home, error } = useHome();
  const [dates, setDates] = useState<Dates>({ preset: "last_7d" });
  const nums = useNumbers(dates);
  const [sheet, setSheet] = useState<HomeSheet>(null);
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const { toast, say } = useHmToast();

  const needKey = goalId ? null : params.get("need");
  const goals = useMemo(() => home?.products.flatMap((p) => p.goals) ?? [], [home]);
  const can = useCan();
  const rows = useMemo(() => (home ? inGoalOrder(waitingRows(home, can)).filter((r) => !gone.has(r.key)) : []), [home, can, gone]);
  const cur = useOpenNeed(rows, needKey);

  // On an iPad, Home always has something beside it: the first running goal, else the first goal.
  const first = goals.find((g) => g.state === "live") ?? goals.find(running) ?? goals[0] ?? null;
  const toFirst = rail && home && !goalId && !needKey && first ? `/goals/${enc(first.id)}` : null;
  useEffect(() => {
    if (toFirst) navigate(toFirst, { replace: true });
  }, [toFirst, navigate]);
  // A need that is no longer waiting (decided elsewhere, or an old link) closes.
  const stale = !!home && !!needKey && !cur;
  useEffect(() => {
    if (stale) navigate("/command", { replace: true });
  }, [stale, navigate]);

  const flow: Omit<NeedFlowProps, "current"> = {
    rows,
    open: (key) => navigate(`/command?need=${enc(key)}`, { replace: true }),
    done: () => (rail ? navigate("/command", { replace: true }) : closeSheet()),
    go: (to) => navigate(to, { replace: !rail }),
    gone: (key) => setGone((s) => new Set(s).add(key)),
    say,
  };

  const list = (
    <div className="pn-screen hm-home">
      {/* Why: beside a goal on an iPad the list is too narrow for the greeting and its buttons on one line, so
          it reads Home, like the other lists there, with the greeting under it. */}
      <PhoneNav
        large
        title={
          rail ? (
            "Home"
          ) : (
            <>
              <span className="hm-big">{greeting()}</span>
              <span className="hm-bar">Home</span>
            </>
          )
        }
        sub={rail ? `${greeting()} · ${longToday()}` : longToday()}
        actions={<HomeActions open={setSheet} />}
      />
      <div className="pn-stack">
        <Summary goals={goals} nums={nums} dates={dates} onDates={() => setSheet("range")} loaded={!!home} />
        {!home ? (
          <p className="pn-note">{error ?? "Loading your goals…"}</p>
        ) : (
          <>
            {rows.length > 0 && (
              <section>
                <div className="pn-sh">
                  <h2>
                    Needs you <Count n={rows.length} />
                  </h2>
                  <Link className="pn-lnk" to="/needs">
                    See all
                  </Link>
                </div>
                <div className="pn-grp">
                  {rows.slice(0, 3).map((w) => (
                    <WaitRow key={w.key} w={w} to={`/command?need=${enc(w.key)}`} sub={shortGoal(w.goal.sentence)} on={rail && needKey === w.key} />
                  ))}
                </div>
              </section>
            )}
            {home.products.map((p) => (
              <ProductGroup key={p.id} p={p} nums={nums} dates={dates} goalOn={rail ? goalId : null} />
            ))}
            {!home.products.length && (
              <Can min="editor">
                <section>
                  <div className="pn-grp">
                    <PnRow to="/products/new" className="accent" icon={<Icon name="plus" />} title="Add a product" />
                  </div>
                </section>
              </Can>
            )}
          </>
        )}
      </div>
    </div>
  );

  const detail = cur ? (
    <NeedPane {...flow} current={cur} />
  ) : (
    (goal ?? (
      <div className="pn-screen">
        {home && !goals.length && (
          <div className="pn-empty">
            <b>No goals yet</b>
            Start one with New.
          </div>
        )}
      </div>
    ))
  );

  return (
    <>
      <Split list={list} detail={detail} showDetail={false} />
      {!rail && cur && <NeedSheet {...flow} current={cur} onClose={() => goBack("/command")} />}
      <HomeSheets
        sheet={sheet}
        close={() => setSheet(null)}
        products={home?.products ?? []}
        dates={dates}
        setDates={setDates}
        newTo={{ to: "/goals/new", coldEmail: !!home?.products.some((p) => p.coldEmail) }}
        say={say}
      />
      {toast}
    </>
  );
}
